/** Typisierter Client für den Worker-Proxy (nie direkt an die ADS-B-Quellen!). */

// In Produktion läuft die SPA auf Cloudflare Pages, der Worker auf einer
// eigenen Domain → Basis-URL per Build-Env setzen (CORS ist im Worker offen).
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export interface Aircraft {
  hex: string;
  /** true, wenn der Datensatz aus dem Open Glider Network stammt */
  ogn?: boolean;
  /** OGN-Gerätetyp (1=Segler, 6=Hängegleiter, 7=Gleitschirm, …) */
  ogn_type?: number;
  flight?: string;
  r?: string;
  t?: string;
  alt_baro?: number | 'ground';
  alt_geom?: number;
  gs?: number;
  track?: number;
  true_heading?: number;
  baro_rate?: number;
  squawk?: string;
  category?: string;
  lat?: number;
  lon?: number;
  seen?: number;
  seen_pos?: number;
  [key: string]: unknown;
}

export interface LiveResponse {
  ac: Aircraft[];
  total: number;
  now: number;
  source: string;
}

export interface AircraftMeta {
  hex: string;
  registration: string | null;
  type_code: string | null;
  type_name: string | null;
  manufacturer: string | null;
  operator: string | null;
  country: string | null;
  photo_url: string | null;
  photo_thumb_url: string | null;
  source: string;
}

export interface WatchlistItem {
  hex: string;
  label: string | null;
  added_at: number;
  /** Dauer der letzten Flüge in Sekunden, chronologisch (für Sparkline) */
  recent_durations: number[];
  open_flight: {
    id: number;
    callsign: string | null;
    started_at: number;
    last_seen_at: number;
  } | null;
}

export interface FlightSummary {
  id: number;
  callsign: string | null;
  status: 'open' | 'closed';
  started_at: number;
  ended_at: number | null;
  origin_icao: string | null;
  dest_icao: string | null;
  max_alt_ft: number | null;
  distance_km: number | null;
  duration_s: number | null;
}

export interface AircraftStats {
  totals: {
    flights: number;
    total_distance_km: number;
    total_duration_s: number;
    max_alt_ft: number | null;
  };
  per_month: Array<{ month: string; flights: number; distance_km: number }>;
  top_routes: Array<{ origin_icao: string; dest_icao: string; count: number }>;
}

export interface TrackPoint {
  ts: number;
  lat: number;
  lon: number;
  alt_ft: number | null;
  gs_kt: number | null;
  track: number | null;
}

export interface FlightTrack {
  flight: FlightSummary & { hex: string };
  points: TrackPoint[];
}

export interface ArchiveSegment {
  callsign: string | null;
  start_ts: number;
  end_ts: number;
  duration_s: number;
  max_alt_ft: number | null;
  points: TrackPoint[];
}

export interface ArchiveDay {
  hex: string;
  date: string;
  r?: string | null;
  t?: string | null;
  available: boolean;
  segments: ArchiveSegment[];
}

export interface OgnAircraft {
  id: string;
  reg: string | null;
  cn: string | null;
  lat: number;
  lon: number;
  alt_ft: number;
  track: number | null;
  gs_kt: number | null;
  vario_ms: number | null;
  ogn_type: number;
  receiver: string | null;
  seen_s: number | null;
}

export interface RouteAirport {
  icao: string;
  iata: string | null;
  name: string | null;
  municipality: string | null;
  lat: number;
  lon: number;
}

export interface RouteDetail {
  callsign: string;
  airline: string | null;
  origin: RouteAirport | null;
  destination: RouteAirport | null;
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(API_BASE + url, { signal });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function sendJson<T>(
  url: string,
  method: 'POST' | 'DELETE',
  token: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(API_BASE + url, {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(body !== undefined && { 'content-type': 'application/json' }),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  point: (lat: number, lon: number, radiusNm: number, signal?: AbortSignal) =>
    // 2 Dezimalstellen (~1 km): deutlich bessere Edge-Cache-Trefferquote beim
    // Panning → schont die Upstream-Quellen (airplanes.live: 1 req/s)
    getJson<LiveResponse>(
      `/api/v1/aircraft/point/${lat.toFixed(2)}/${lon.toFixed(2)}/${Math.round(radiusNm)}`,
      signal,
    ),
  hex: (hexes: string[], signal?: AbortSignal) =>
    getJson<LiveResponse>(`/api/v1/aircraft/hex/${hexes.join(',')}`, signal),
  callsign: (cs: string, signal?: AbortSignal) =>
    getJson<LiveResponse>(`/api/v1/aircraft/callsign/${encodeURIComponent(cs)}`, signal),
  reg: (reg: string, signal?: AbortSignal) =>
    getJson<LiveResponse>(`/api/v1/aircraft/reg/${encodeURIComponent(reg)}`, signal),
  meta: (hex: string, signal?: AbortSignal) =>
    getJson<{ meta: AircraftMeta | null }>(`/api/v1/aircraft/meta/${hex}`, signal).then(
      (r) => r.meta,
    ),
  watchlist: (signal?: AbortSignal) =>
    getJson<{ items: WatchlistItem[] }>('/api/v1/watchlist', signal).then((r) => r.items),
  watchlistAdd: (hex: string, label: string | null, token: string) =>
    sendJson<{ ok: boolean }>('/api/v1/watchlist', 'POST', token, { hex, label }),
  watchlistRemove: (hex: string, token: string) =>
    sendJson<{ ok: boolean }>(`/api/v1/watchlist/${hex}`, 'DELETE', token),
  flights: (hex: string, signal?: AbortSignal) =>
    getJson<{ flights: FlightSummary[] }>(`/api/v1/history/${hex}/flights`, signal).then(
      (r) => r.flights,
    ),
  stats: (hex: string, signal?: AbortSignal) =>
    getJson<AircraftStats>(`/api/v1/history/${hex}/stats`, signal),
  track: (flightId: number, signal?: AbortSignal) =>
    getJson<FlightTrack>(`/api/v1/history/flight/${flightId}/track`, signal),
  /** Hex ODER Registrierung auflösen — funktioniert auch am Boden. */
  find: (query: string, signal?: AbortSignal) =>
    getJson<{ hex: string; meta: AircraftMeta | null }>(
      `/api/v1/aircraft/find/${encodeURIComponent(query)}`,
      signal,
    ),
  route: (callsign: string, signal?: AbortSignal) =>
    getJson<{ route: RouteDetail | null }>(
      `/api/v1/aircraft/route/${encodeURIComponent(callsign)}`,
      signal,
    ).then((r) => r.route),
  airports: (bbox: string, signal?: AbortSignal) =>
    getJson<GeoJSON.FeatureCollection>(`/api/v1/airports/${bbox}`, signal),
  /** FLARM-Traffic (Segler/ULs) aus dem Open Glider Network */
  ogn: (bbox: string, signal?: AbortSignal) =>
    getJson<{ ac: OgnAircraft[]; now: number }>(`/api/v1/ogn/${bbox}`, signal).then((r) => r.ac),
  /** Rückwirkende Tages-Historie (adsb.lol globe_history) — unabhängig von der Watchlist */
  archive: (hex: string, date: string, signal?: AbortSignal) =>
    getJson<ArchiveDay>(`/api/v1/history/archive/${hex}/${date}`, signal),
  /** Kontrollzonen + VFR-Meldepunkte (openAIP); available:false ohne API-Key */
  airspace: (bbox: string, signal?: AbortSignal) =>
    getJson<GeoJSON.FeatureCollection & { available: boolean }>(
      `/api/v1/airspace/${bbox}`,
      signal,
    ),
};

// Dedupe-Schwellen: gleiche Maschine kann per ADS-B (ICAO-Hex) UND
// FLARM (eigene ID) senden — dann stimmen die IDs nicht überein.
const DUP_POS_DEG = 0.015; // ~1,5 km (Zeitversatz der Quellen bei 200+ kt)
const DUP_ALT_FT = 1000;

/**
 * OGN-Beacons zu den ADS-B-Fliegern mergen, Duplikate raus:
 * 1. gleiche ID (ADS-B-Geräte tauchen in OGN mit ihrem ICAO-Hex auf)
 * 2. gleiche Registrierung (ADS-B + separater FLARM-Tracker an Bord)
 * 3. fast gleiche Position + Höhe (Reg fehlt auf einer Seite)
 */
export function mergeOgn(adsb: Aircraft[], ogn: OgnAircraft[]): Aircraft[] {
  const knownIds = new Set(adsb.map((a) => a.hex));
  const knownRegs = new Set(
    adsb.map((a) => a.r?.trim().toUpperCase()).filter((r): r is string => !!r),
  );
  const out = [...adsb];
  for (const o of ogn) {
    if (!o.id || knownIds.has(o.id)) continue;
    if (o.reg && knownRegs.has(o.reg.trim().toUpperCase())) continue;
    const positionDup = adsb.some((a) => {
      if (typeof a.lat !== 'number' || typeof a.lon !== 'number') return false;
      if (Math.abs(a.lat - o.lat) > DUP_POS_DEG) return false;
      const lonScale = Math.cos((o.lat * Math.PI) / 180);
      if (Math.abs(a.lon - o.lon) * lonScale > DUP_POS_DEG) return false;
      const altFt = typeof a.alt_baro === 'number' ? a.alt_baro : a.alt_baro === 'ground' ? 0 : null;
      return altFt === null || Math.abs(altFt - o.alt_ft) <= DUP_ALT_FT;
    });
    if (!positionDup) out.push(ognToAircraft(o));
  }
  return out;
}

/** OGN-Beacon → einheitliches Aircraft-Format der Live-Karte. */
export function ognToAircraft(o: OgnAircraft): Aircraft {
  return {
    hex: o.id,
    ogn: true,
    ogn_type: o.ogn_type,
    r: o.reg ?? undefined,
    flight: o.reg ?? o.cn ?? undefined,
    lat: o.lat,
    lon: o.lon,
    alt_baro: o.alt_ft,
    gs: o.gs_kt ?? undefined,
    track: o.track ?? undefined,
    // Vario m/s → ft/min, damit das Panel es als V/R anzeigt
    baro_rate: o.vario_ms !== null ? Math.round(o.vario_ms * 196.85) : undefined,
    seen: o.seen_s ?? undefined,
  };
}
